/* eslint-disable no-console */
import React, {useEffect, useState} from 'react';
import {Modal, Platform, View} from 'react-native';
import * as KeyChain from 'react-native-keychain';
import {noop} from 'lodash';

import Button from '/component/base/Button';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import ModalHeader from '/component/layout/ModalHeader';
import {BiometricsTestIds} from '/constant/TestIDs';
import {
  BIOMETRIC_REQUESTED,
  HAS_OPTED_INTO_BIOMETRICS,
} from '/enum/storage.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {useStorage} from '/util/async-storage/useStorage';
import {getErrorTrackingUtilDefaultInstance} from '/util/error-tracking/error-tracking.util';
import {error as consoleError} from '/util/logger.util';
import testProperties from '/util/testing/testProperties';

import FaceIDModal from '../FaceIDModal/FaceIDModal';

import {styles} from './EnableFaceID.style';
interface EnableFaceIDProps {
  onClose: () => void;
  biometricsOptIn: any;
}

const EnableFaceID: React.FC<EnableFaceIDProps> = ({
  onClose = noop,
  biometricsOptIn,
}) => {
  const {ts} = useTranslation('screen.EnableFaceID');
  const [isModalVisible, setModalVisible] = useState(false);
  const [isFaceIDModalVisible, setFaceIDModalVisible] = useState(false);

  const {setValue: setBiometricsOptInValue} = useStorage({
    key: HAS_OPTED_INTO_BIOMETRICS,
    defaultValue: false,
  });
  const {setValue: setbiometricsWasRequested} = useStorage({
    key: BIOMETRIC_REQUESTED,
    defaultValue: false,
  });

  async function isBiometricEnabled(): Promise<boolean> {
    try {
      setbiometricsWasRequested(true);

      // Attempt to retrieve a password using biometrics
      const credentials = await KeyChain.getGenericPassword({
        service: 'connect-app-biometrics-login',
      });
      if (credentials) {
        return true;
      } else {
        return false;
      }
    } catch (error: any) {
      // Handle cases where biometrics are disabled or unavailable
      if (
        error.message.includes('User canceled the operation') ||
        error.message.includes('Not available')
      ) {
        getErrorTrackingUtilDefaultInstance().trackError(
          error,
          `Biometrics are disabled or unavailable:, ${error.message}`,
        );
        return false;
      }

      getErrorTrackingUtilDefaultInstance().trackError(
        error,
        `Biometricsunexpected error occurred:, ${error.message}`,
      );
      return false;
    }
  }

  const handleBiometric = () => {
    KeyChain.getSupportedBiometryType()
      .then(resultObject => {
        // Handle no biometric support
        if (resultObject === null) {
          console.log('No biometric support available.');

          if (Platform.OS === 'ios') {
            setFaceIDModalVisible(true);
            isBiometricEnabled()
              .then(enabled => setBiometricsOptInValue(enabled))
              .catch(err =>
                console.error('Error checking biometric enabled:', err),
              );
          } else {
            setBiometricsOptInValue(false); // Default to false if no biometrics
            onClose();
          }
          return;
        }
        // Handle biometric types
        if (
          resultObject === 'FaceID' ||
          resultObject === 'TouchID' ||
          resultObject === 'Fingerprint'
        ) {
          isBiometricEnabled()
            .then(enabled => {
              setBiometricsOptInValue(enabled);
              onClose();
            })
            .catch(err =>
              consoleError('Error checking biometric enabled:', err),
            );
          return;
        }
        setBiometricsOptInValue(false); // Fallback for unsupported biometric types
        onClose();
      })
      .catch(error => {
        consoleError('Biometric error', error);
        setBiometricsOptInValue(false); // Handle errors gracefully
        onClose();
      });
  };

  const handleEnablePress = () => {
    if (Platform.OS === 'ios') {
      setModalVisible(true);
    } else {
      handleBiometric();
    }
  };

  useEffect(() => {
    if (isModalVisible) {
      handleBiometric();
    }
  }, [isModalVisible]);

  return (
    <>
      <ModalHeader
        title={
          Platform.OS === 'ios'
            ? ts('Enable Face ID', 'Enable Face ID')
            : ts('Enable Touch ID', 'Enable Touch ID')
        }
        accessibilityViewIsModal
        style={{base: styles.base, headerText: styles.headerText}}
        {...testProperties(BiometricsTestIds.enableBiometricsScreen)}>
        <View style={styles.content}>
          <Icon
            name={Platform.OS === 'ios' ? 'faceID' : 'touchID'}
            style={styles.icon}
          />
          <Text
            style={styles.enableText}
            {...testProperties(BiometricsTestIds.enableBiometricsScreenHeader)}>
            {Platform.OS === 'ios'
              ? 'Enabling Face ID helps you login faster'
              : 'Enabling Touch ID helps you login faster'}
          </Text>
          <Text
            style={styles.turnonText}
            {...testProperties(
              BiometricsTestIds.enableBiometricsScreenDescription,
            )}>
            You can turn this feature on or off at any time under Settings.
          </Text>
          <View style={styles.modalFooter}>
            <Button
              onPress={handleEnablePress}
              style={{base: styles.enableButton}}
              {...testProperties(
                BiometricsTestIds.enableBiometricsScreenEnableButton,
              )}>
              Enable
            </Button>
            <Button
              type="textLink"
              onPress={onClose}
              style={{base: styles.buttonBox, text: styles.textStyle}}
              {...testProperties(
                BiometricsTestIds.enableBiometricsScreenNotNowButton,
              )}>
              Not now
            </Button>
          </View>
        </View>
      </ModalHeader>
      <Modal
        animationType="fade"
        transparent={true}
        visible={isModalVisible}
        onRequestClose={() => setModalVisible(false)}>
        {isFaceIDModalVisible && Platform.OS === 'ios' && (
          <FaceIDModal
            onClose={onClose}
            NotnowButton={onClose}
            biometricsOptIn={biometricsOptIn}
          />
        )}
      </Modal>
    </>
  );
};

export default EnableFaceID;
